/**
 * HTTP client for the Mimic server (HARNESS §7 "HTTP surface").
 *
 *   POST /api/tools/:name  { input, confirmToken? }
 *     → 200 { result }
 *     → 409 { error: "confirmation_required", message, details: { confirmToken, expiresAt } }
 *     → 409 { error: "human_approval_required", message, details: { pendingActionId, summary } }
 *     → 4xx/5xx { error, message, details }
 *
 * `callTool` is the raw call. `runTool` is the interactive call used by every UI mutation: it opens the
 * global confirmation modal on `confirmation_required`, re-calls with the token after the human
 * confirms, and toasts (with a link to Decisions) on `human_approval_required`.
 */
import { requestConfirmation } from "./confirm-store";
import { toast } from "./toast-store";
import type { ToolName } from "./tools";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** The server does not know this tool yet (module not shipped) — UI shows a "waiting on backend" state. */
  get isUnknownTool(): boolean {
    return /unknown tool/i.test(this.message) || this.code === "unknown_tool";
  }
  get isNetwork(): boolean {
    return this.code === "network";
  }
}

/** Thrown when the human dismisses a confirmation modal — callers should treat it as a no-op. */
export class ConfirmationCancelled extends Error {
  constructor() {
    super("Confirmation cancelled");
    this.name = "ConfirmationCancelled";
  }
}

export function isCancelled(e: unknown): boolean {
  return e instanceof ConfirmationCancelled;
}

let unauthorizedHandler: (() => void) | null = null;
/** Registered by the auth provider: called on any 401 (session expired) → redirect to /login. */
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: { error?: string; message?: string; details?: Record<string, unknown> } = {};
  const text = await res.text().catch(() => "");
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    body = { message: text.slice(0, 300) };
  }
  // Vite dev proxy answers 500/502/504 with an empty/plain body when the API is down.
  if ((res.status === 502 || res.status === 504 || res.status === 500) && !body.error && /ECONNREFUSED|proxy|socket hang up|^$/i.test(body.message ?? "")) {
    return new ApiError(res.status, "network", "Can't reach the Mimic server. Is it running?");
  }
  return new ApiError(res.status, body.error ?? `http_${res.status}`, body.message ?? res.statusText ?? "Request failed", body.details ?? {});
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /** Do not trigger the global 401 redirect (login/me/invite endpoints). */
  noAuthRedirect?: boolean;
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: opts.method ?? (opts.body !== undefined ? "POST" : "GET"),
      credentials: "same-origin",
      headers: opts.body !== undefined ? { "content-type": "application/json", accept: "application/json" } : { accept: "application/json" },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error)?.name === "AbortError") throw e;
    throw new ApiError(0, "network", "Can't reach the Mimic server. Is it running?");
  }
  if (res.status === 401 && !opts.noAuthRedirect) {
    unauthorizedHandler?.();
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") ?? "";
  if (!ct.includes("json")) return (await res.text()) as T;
  return (await res.json()) as T;
}

export interface CallToolOptions {
  confirmToken?: string;
  signal?: AbortSignal;
}

/** Raw tool call. Throws ApiError (incl. 409 confirmation_required). */
export async function callTool<T = unknown>(name: ToolName | string, input: Record<string, unknown> = {}, opts: CallToolOptions = {}): Promise<T> {
  const body: Record<string, unknown> = { input };
  if (opts.confirmToken) body.confirmToken = opts.confirmToken;
  const res = await api<{ result: T } | T>(`/api/tools/${encodeURIComponent(name)}`, { method: "POST", body, signal: opts.signal });
  if (res && typeof res === "object" && "result" in (res as Record<string, unknown>)) return (res as { result: T }).result;
  return res as T;
}

export interface RunToolOptions extends CallToolOptions {
  /** Shown in the confirmation modal header (defaults to a humanised tool name). */
  label?: string;
}

/**
 * Interactive tool call with the two-step confirmation flow + human-approval handling.
 * Resolves with the tool result; rejects with ConfirmationCancelled if the human backs out.
 */
export async function runTool<T = unknown>(name: ToolName | string, input: Record<string, unknown> = {}, opts: RunToolOptions = {}): Promise<T> {
  // One admission key per explicit action, retained through the confirmation handshake.
  // Never submit on mount or automatically retry uncertain paid work.
  if (["ideas.accept", "ideas.from_reference", "creatives.generate_matrix", "creatives.generate_video", "creatives.plan_video", "creatives.iterate", "creatives.regenerate_asset", "creatives.edit_asset", "creatives.storyboard_preview"].includes(name) && !(name === "ideas.from_reference" && input.generate === false) && !input.generationRequestId) {
    input = { ...input, generationRequestId: crypto.randomUUID() };
  }
  try {
    return await callTool<T>(name, input, opts);
  } catch (e) {
    if (!(e instanceof ApiError)) throw e;
    if (e.code === "confirmation_required") {
      const token = typeof e.details.confirmToken === "string" ? e.details.confirmToken : undefined;
      if (!token) throw e;
      const ok = await requestConfirmation({
        tool: name,
        label: opts.label,
        summary: e.message,
        expiresAt: typeof e.details.expiresAt === "string" ? e.details.expiresAt : undefined,
        input,
      });
      if (!ok) throw new ConfirmationCancelled();
      return callTool<T>(name, input, { ...opts, confirmToken: token });
    }
    if (e.code === "human_approval_required") {
      toast({
        tone: "warning",
        title: "Queued for human approval",
        body: typeof e.details.summary === "string" ? e.details.summary : e.message,
        action: { label: "Open Decisions", href: "/decisions" },
      });
    }
    throw e;
  }
}

/** Human-readable error text for toasts and error states. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.isUnknownTool) return `${e.message} — this module isn't available on the server yet.`;
    if (e.code === "policy_violation" && typeof e.details.rule === "string") return `${e.message} (rule: ${e.details.rule})`;
    return e.message;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}

/** Read a File as base64 (no data: prefix) for JSON upload tools. */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("Could not read file"));
    reader.onload = () => {
      const s = String(reader.result ?? "");
      resolve(s.includes(",") ? s.slice(s.indexOf(",") + 1) : s);
    };
    reader.readAsDataURL(file);
  });
}

// ─── Media uploads ───────────────────────────────────────────────────────────

export type UploadPurpose = "reference" | "creative" | "brand_asset" | "chat";

/** Result of POST /api/uploads — pass `key` as `storageKey` (or `url`) to the tool that consumes the file. */
export interface UploadedMedia {
  key: string;
  url: string;
  contentType: string;
  bytes: number;
  kind: "image" | "video" | string;
  purpose: UploadPurpose;
  brandId: string | null;
  name: string | null;
}

/**
 * Multipart upload to `POST /api/uploads` (fields: file, brandId, purpose). The server sniffs the type
 * (PNG/JPEG/WebP/GIF/MP4/MOV/WebM), stores it and returns a storage key. Tools such as
 * creatives.upload / assets.upload / references.upload and chat attachments take that key.
 */
export function uploadFile(
  file: File,
  opts: { brandId?: string | null; purpose: UploadPurpose; onProgress?: (fraction: number) => void; signal?: AbortSignal },
): Promise<UploadedMedia> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append("file", file, file.name);
    if (opts.brandId) form.append("brandId", opts.brandId);
    form.append("purpose", opts.purpose);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/uploads");
    xhr.withCredentials = true;
    xhr.responseType = "text";
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) opts.onProgress?.(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new ApiError(0, "network", "Upload failed — can't reach the Mimic server."));
    xhr.onabort = () => reject(new DOMException("Upload aborted", "AbortError"));
    xhr.onload = () => {
      let body: Record<string, unknown> = {};
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch {
        /* non-JSON */
      }
      if (xhr.status === 401) unauthorizedHandler?.();
      if (xhr.status >= 200 && xhr.status < 300) {
        opts.onProgress?.(1);
        resolve(body as unknown as UploadedMedia);
      } else {
        reject(new ApiError(xhr.status, String(body.error ?? `http_${xhr.status}`), String(body.message ?? "Upload failed"), (body.details as Record<string, unknown>) ?? {}));
      }
    };
    opts.signal?.addEventListener("abort", () => xhr.abort());
    xhr.send(form);
  });
}
